import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, LayoutGrid, Mail, Settings, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { LogoMark } from "../components/Logo";
import { PostsTab } from "../components/admin/PostsTab";
import { SectionsTab } from "../components/admin/SectionsTab";
import { InboxTab } from "../components/admin/InboxTab";
import { SettingsTab } from "../components/admin/SettingsTab";

const TABS = [
  { id: "posts", label: "Entries", icon: FileText },
  { id: "sections", label: "Sections", icon: LayoutGrid },
  { id: "inbox", label: "Inbox", icon: Mail },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { refresh } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = useState("posts");

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#070709] relative" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-[#232530] bg-[rgba(7,7,9,0.92)] backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={26} />
            <span className="font-display text-sm tracking-[0.2em] uppercase text-[#ede8e1]">Sanctum</span>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716c] hidden sm:inline">
              — {user?.email}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} data-testid="admin-view-site-button"
              className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#a8a29e] hover:text-[#581313] transition-colors">
              View Site
            </button>
            <button onClick={doLogout} data-testid="admin-logout-button"
              className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase px-4 py-2 border border-[#581313]/50 text-[#ede8e1] hover:bg-[#581313] transition-colors">
              <LogOut size={13} /> Depart
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <nav className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-2.5 border transition-colors ${
                tab === id
                  ? "bg-[#581313] border-[#581313]/40 text-[#ede8e1]"
                  : "bg-[#0d0e12] border-[#232530] text-[#a8a29e] hover:border-[#581313]/50"
              }`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </nav>
        {tab === "posts" && <PostsTab />}
        {tab === "sections" && <SectionsTab onChanged={refresh} />}
        {tab === "inbox" && <InboxTab />}
        {tab === "settings" && <SettingsTab onSaved={refresh} />}
      </div>
    </div>
  );
}
